<script setup lang="ts">
import { useTemplateRef } from "vue";

import {
  TEditSiteConsumptionInfosBody,
  type TSiteConsumptionInfos,
} from "@/data/api/kili/consumption/types/consumption-infos.type";
import { useTypedIntl } from "@/locales/typed-intl";

import EditSiteDetailsForm from "@/components/tools/site-details/edit-site-details/edit-site-details-form.vue";
import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";
import BaseDialog from "@/components/ui/dialog/base-dialog.vue";

interface IProps {
  siteName?: string;
  siteConsumptionInfos?: TSiteConsumptionInfos;
  isLoading?: boolean;
}

interface IEmits {
  (e: "submit", payload: TEditSiteConsumptionInfosBody): void;
}

const visible = defineModel<boolean>();
const props = defineProps<IProps>();
const emits = defineEmits<IEmits>();

const { t, k } = useTypedIntl();

const editSiteDetailsForm = useTemplateRef("editSiteDetailsForm");

async function handleSubmit() {
  const payload = await editSiteDetailsForm.value?.submit();

  if (!payload) return;

  emits("submit", payload);
}
</script>

<template>
  <BaseDialog
    v-model:visible="visible"
    class="edit-site-details-dialog"
    modal
    size="small"
  >
    <template #header>
      <h4 class="flex w-full font-semibold">
        {{ t(k.tools.siteManagement.siteDetails.edition.title) }}
      </h4>
    </template>

    <EditSiteDetailsForm
      ref="editSiteDetailsForm"
      :site-name="props.siteName"
      :site-consumption-infos="props.siteConsumptionInfos"
      :is-loading="props.isLoading"
    />

    <template #footer>
      <div class="flex w-full gap-2">
        <HospitalityButton
          class="w-full"
          data-test-id="edit-site-details-cancel-button"
          :label="t(k.global.misc.cancel)"
          severity="secondary"
          @click="visible = false"
        />
        <HospitalityButton
          class="w-full"
          data-test-id="edit-site-details-submit-button"
          :disabled="props.isLoading"
          :label="t(k.global.misc.submit)"
          type="submit"
          @click="handleSubmit"
        />
      </div>
    </template>
  </BaseDialog>
</template>
